import {
  ReactNode,
  useEffect,
  createContext,
  useCallback,
  useState,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

import { Save, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { clearFieldLabels } from "@/hooks/useFormValidation";

const ANIMATION_DURATION = 200; // Duración de la animación del Dialog

// Clases de variante para FormDialog - solo cambios de color sin afectar posicionamiento
const FORM_DIALOG_VARIANTS = {
  default:
    "bg-accent dark:from-popover dark:via-popover dark:to-accent/10 border-border",
  destructive:
    "bg-gradient-to-tl from-red-50 via-red-100/50 to-red-50 dark:from-popover dark:via-red-950/20 dark:to-popover border-destructive/20",
  secondary:
    "bg-gradient-to-tl from-secondary/30 via-secondary/10 to-secondary/30 dark:from-popover dark:via-secondary/10 dark:to-popover border-secondary/20",
  outline:
    "bg-gradient-to-tl from-background via-accent/50 to-background dark:from-popover dark:via-popover dark:to-accent/10 border-input/50",
  success:
    "bg-gradient-to-tl from-green-50 via-green-100/50 to-green-50 dark:from-popover dark:via-green-950/20 dark:to-popover border-green-500/20",
  warning:
    "bg-gradient-to-tl from-yellow-50 via-yellow-100/50 to-yellow-50 dark:from-popover dark:via-yellow-950/20 dark:to-popover border-yellow-500/20",
  info: "bg-gradient-to-tl from-blue-50 via-blue-100/50 to-blue-50 dark:from-popover dark:via-blue-950/20 dark:to-popover border-blue-500/20",
} as const;

// Context para pasar formId automáticamente a los campos
export const FormContext = createContext<{ formId: string }>({ formId: "" });

type FormDialogProps = {
  // Dialog props
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;

  // Form props
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  children: ReactNode;

  // Actions props (opcionales)
  submitLabel?: string;
  loadingLabel?: string;
  cancelLabel?: string;
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  footerStart?: ReactNode;

  // Layout props (opcionales)
  className?: string;
  maxWidth?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
  maxHeight?: string;
  /** Altura fija para el contenido del drawer en mobile (ej: "400px", "50vh"). Evita saltos de tamaño entre tabs. */
  drawerHeight?: string;
  formId?: string;
  disabledAction?: boolean;
  variant?: keyof typeof FORM_DIALOG_VARIANTS;
  /** Level of nesting for z-index handling (0 = top level, 1+ = nested modals). Default: 0 */
  nestedLevel?: number;
};

const MAX_WIDTHS = {
  sm: "lg:max-w-sm",
  md: "lg:max-w-md",
  lg: "lg:max-w-lg",
  xl: "lg:max-w-xl",
  "2xl": "lg:max-w-2xl",
  "3xl": "lg:max-w-3xl",
  "4xl": "lg:max-w-4xl",
  "5xl": "lg:max-w-5xl",
  "6xl": "lg:max-w-6xl",
  "7xl": "lg:max-w-7xl",
};

export function FormDialog({
  isOpen,
  onClose,
  title,
  description,
  showCloseButton = true,
  onSubmit,
  isLoading,
  children,
  submitLabel = "Guardar",
  loadingLabel = "Guardando...",
  drawerHeight,
  cancelLabel = "Cancelar",
  showCancelButton = true,
  footerStart,
  maxWidth = "xl",
  maxHeight = "90dvh",
  formId = "form-dialog",
  disabledAction = false,
  className,
  variant = "default",
  nestedLevel = 0,
}: FormDialogProps) {
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Generar un formId único por instancia para evitar colisiones cuando hay varios formularios abiertos.
  // Usamos un ref combinado con un contador global en `globalThis` y un timestamp corto.
  const instanceIdRef = useRef<string | null>(null);
  if (!instanceIdRef.current) {
    const counter = (globalThis as any).__formDialogInstanceCounter || 0;
    (globalThis as any).__formDialogInstanceCounter = counter + 1;
    instanceIdRef.current = `${formId}-${(globalThis as any).__formDialogInstanceCounter}-${Date.now()
      .toString(36)
      .slice(-6)}`;
  }
  const resolvedFormId = instanceIdRef.current!;
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Sincronizar estado interno con prop externa
  useEffect(() => {
    if (isOpen) {
      setInternalIsOpen(true);
    } else {
      setInternalIsOpen(false);
    }
  }, [isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Prevenir cierre durante submit
      if (!open && isLoading) {
        return;
      }

      if (!open) {
        // Iniciar animación de cierre inmediatamente
        setInternalIsOpen(false);

        // Limpiar timeout anterior si existe
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }

        // Esperar a que termine la animación antes de llamar onClose
        closeTimeoutRef.current = setTimeout(() => {
          // Limpiar los labels registrados para este formulario cuando el diálogo
          // haya terminado de cerrarse (evita colisiones si se abren otros forms).
          clearFieldLabels(resolvedFormId);
          onClose();
          closeTimeoutRef.current = null;
        }, ANIMATION_DURATION);
      }
    },
    [isLoading, onClose],
  );

  // Limpiar timeout y los labels del formulario al desmontar
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      // Asegurarse de limpiar también cualquier label registrado para esta instancia
      // en caso de que el componente se desmonte antes de completar el cierre.
      clearFieldLabels(resolvedFormId);
    };
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onSubmit(e);
    },
    [onSubmit],
  );

  const isMobileRef = useRef(window.innerWidth < 768);
  const isMobile = isMobileRef.current;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Prevenir scroll del body cuando el drawer está abierto en mobile
  // El contenido interno hace scroll, no el sheet
  useEffect(() => {
    if (!isMobile || !internalIsOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    // Also listen to viewport changes and scroll focused element into view
    const viewport = window.visualViewport;
    if (viewport) {
      const handleViewportChange = () => {
        // Cuando el teclado abre/cierra, scroll el input focused a la vista SOLO si no está visible
        const focused = document.activeElement as
          | HTMLInputElement
          | HTMLTextAreaElement;
        if (
          focused &&
          (focused.tagName === "INPUT" || focused.tagName === "TEXTAREA") &&
          scrollContainerRef.current
        ) {
          // Delay para que el layout se actualice primero
          setTimeout(() => {
            const focusedRect = focused.getBoundingClientRect();
            const containerRect = scrollContainerRef.current!.getBoundingClientRect();
            const scrollTop = scrollContainerRef.current!.scrollTop;
            
            // Verificar si el input está visible en el viewport del container
            const isVisible =
              focusedRect.top >= containerRect.top &&
              focusedRect.bottom <= containerRect.bottom;
            
            // Solo scrollear si el input NO está visible
            if (!isVisible) {
              const relativeTop = focusedRect.top - containerRect.top + scrollTop;
              // Scroll with padding for header visibility (140px = header height + safe margin)
              scrollContainerRef.current!.scrollTop = Math.max(0, relativeTop - 140);
            }
            // Si está visible, permitir que el usuario haga scroll manual libremente
          }, 50);
        }
      };
      viewport.addEventListener("resize", handleViewportChange);
      return () => {
        viewport.removeEventListener("resize", handleViewportChange);
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
      };
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, [isMobile, internalIsOpen]);

  const submitButton = (
    <Button
      type="submit"
      form={resolvedFormId}
      disabled={isLoading || disabledAction}
      className="w-full transition-opacity duration-200 ease-in-out disabled:opacity-50"
    >
      {isLoading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Save className="mr-2 h-4 w-4" />
      )}
      {isLoading ? loadingLabel : submitLabel}
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet
        open={internalIsOpen}
        onOpenChange={(open: boolean) => {
          if (!open && !isLoading) handleOpenChange(false);
        }}
      >
        <SheetContent
          side="bottom"
          className="flex flex-col p-0 rounded-t-[10px]"
          style={{
            height: "100dvh",
            zIndex: 1002 + nestedLevel * 100,
          }}
          // Deshabilitar el close con el botón X si quieres preventClose
          onInteractOutside={(e) => {
            // Permitir interacción con portals de Radix (Popover, Select, Command, etc.)
            const target = e.target as HTMLElement;
            const isRadixPortal =
              target.closest("[data-radix-popper-content-wrapper]") ||
              target.closest("[data-radix-select-viewport]") ||
              target.closest("[cmdk-list]") ||
              target.closest("[role='listbox']") ||
              target.closest("[role='option']") ||
              target.closest("[data-radix-select-content]") ||
              target.closest("[data-radix-popper-content]") ||
              target.closest(".popover-content") ||
              target.closest('[class*="popover"]');
            if (!isRadixPortal) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => isLoading && e.preventDefault()}
          onPointerDownOutside={(e) => {
            // Permitir pointer events en portals de Radix
            const target = e.target as HTMLElement;
            const isRadixPortal =
              target.closest("[data-radix-popper-content-wrapper]") ||
              target.closest("[data-radix-select-viewport]") ||
              target.closest("[cmdk-list]") ||
              target.closest("[role='listbox']") ||
              target.closest("[role='option']") ||
              target.closest("[data-radix-select-content]") ||
              target.closest("[data-radix-popper-content]") ||
              target.closest(".popover-content") ||
              target.closest('[class*="popover"]');
            if (!isRadixPortal) e.preventDefault();
          }}
        >
          {/* Header - sticky para mantenerlo visible cuando scrolleas */}
          <SheetHeader className="text-left pb-3 pt-4 px-4 flex-shrink-0 border-b sticky top-0 bg-background/95 backdrop-blur-sm z-10">
            <SheetTitle className="text-base">{title}</SheetTitle>
            {description && (
              <p className="text-xs text-muted-foreground line-clamp-1">
                {description}
              </p>
            )}
          </SheetHeader>

          {/* Contenido con scroll - el contenido interno scrollea, no el sheet */}
          <div
            ref={scrollContainerRef}
            className="form-scroll-container flex-1 overflow-y-auto px-4 pb-20 min-h-0"
            style={drawerHeight ? { maxHeight: drawerHeight } : undefined}
          >
            <FormContext.Provider value={{ formId: resolvedFormId }}>
              <form
                id={resolvedFormId}
                onSubmit={handleSubmit}
                className="space-y-3 pt-3"
              >
                {children}
              </form>
            </FormContext.Provider>
          </div>

          {/* Footer */}
          <SheetFooter className="pt-2 pb-4 px-4 flex-shrink-0 border-t bg-background flex-row gap-2">
            {footerStart}
            {showCancelButton && (
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isLoading}
                className="flex-1"
              >
                {cancelLabel}
              </Button>
            )}
            <div className="flex-1">{submitButton}</div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={internalIsOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          MAX_WIDTHS[maxWidth],
          "flex flex-col",
          FORM_DIALOG_VARIANTS[variant || "default"],
          className,
        )}
        style={{ maxHeight }}
        showCloseButton={showCloseButton}
        nestedLevel={nestedLevel}
      >
        {/* Header fijo */}
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <FormContext.Provider value={{ formId: resolvedFormId }}>
          <form
            id={resolvedFormId}
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 min-h-0"
          >
            {/* Contenido del formulario con scroll */}
            <div className="flex-1 overflow-y-auto px-1 space-y-6 max-h-[calc(100dvh-16rem)]">
              {children}
            </div>

            {/* Botones de acción fijos */}
            <div className="flex-shrink-0 flex justify-between pt-4 mt-4 border-t">
              <div>{footerStart}</div>
              <div className="flex gap-4">
                {showCancelButton && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isLoading}
                  >
                    {cancelLabel}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isLoading || disabledAction}
                  className="transition-opacity duration-200 ease-in-out disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {isLoading ? loadingLabel : submitLabel}
                </Button>
              </div>
            </div>
          </form>
        </FormContext.Provider>
      </DialogContent>
    </Dialog>
  );
}

// Para mantener compatibilidad
import { FieldGroup } from "./FieldGroup";
import { InputField } from "./InputField";
import { SelectField } from "./SelectField";
import { TextareaField } from "./InputTextArea";
import { NumberInputField } from "./NumberInputField";
import { FormField } from "./FormField";
import { ArrayInputField } from "./ArrayInputField";
import { SelectMultipleField } from "./SelectMultipleField";

export {
  FieldGroup,
  InputField,
  SelectField,
  TextareaField,
  NumberInputField,
  FormField,
  ArrayInputField,
  SelectMultipleField,
};
